// PLEX-6303: a conversation whose turn is still running on the backend, seen from a view
// that isn't attached to it (a reload, another tab). Shared by the v2 loader
// (pages/workspace/useConversationPersistence.js) and the Classic chat (components/ChatSession.js).
import { useEffect } from "react";
import { reaction, runInAction } from "mobx";
import { mayBeMidTurn } from "./LLMClient";
import { fetchTurnRunning, fetchTurnStatus } from "../queries/search";

// How often a view showing a turn that is running elsewhere asks whether it ended.
export const RUNNING_TURN_POLL_MS = 15000;

// Only a stored conversation's id can be asked about; a search history item's id is a URL.
const CONVERSATION_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const isConversationId = (id) => typeof id === "string" && CONVERSATION_ID_RE.test(id);

// loadConversation normalises the turns it is given in place (legacy roles, sub-agent turns),
// so a payload loaded twice must be copied for the first load.
export const copyOf = (value) => (value == null ? value : JSON.parse(JSON.stringify(value)));

/**
 * Ask whether `conversationId`'s turn is still running, and mark the engine when it is.
 * True only for a definite yes: an ended turn, or one the backend can't answer for (a
 * backend without rd #244), is false, so the caller goes on exactly as before.
 */
export const markIfTurnRunning = async (client, conversationId) => {
  if (!isConversationId(conversationId)) return false;
  if (await fetchTurnRunning(conversationId, client.auth) !== true) return false;
  client.markTurnRunning?.();
  return true;
};

/**
 * PLEX-6303 (Phase 2): attach `client` to `conversationId`'s running turn, from the stored
 * record's last event on. Once the backend writes the record (PLEX-6383), its `lastEventId`
 * is the id of the "message" event of the last turn it stored, so the stream's replay picks
 * up exactly where the loaded turns end, and the rest of the turn streams in as if the page
 * had never reloaded. The stream is addressed by conversation id (rd #244), so this works
 * from any device, with no cursor in this browser.
 *
 * Seeded in attemptStreamResume's order: the streamId setter clears the cursor and the
 * resume flags on its null-to-truthy edge, so they are set after it. The turn is one the
 * backend writes, so its nodes take the backend's ids and saves stay navigation-only.
 */
export const reattachConversation = (client, conversationId, { lastEventId, agentId }) => {
  runInAction(() => {
    client.active = true;
    client.streamId = conversationId;
    client._lastEventId = lastEventId || "";
    client._agentId = agentId;
    client.historyPersisted = true;
    client._resumedAt = Date.now();
  });
};

/**
 * PLEX-6303 (Phase 2): for a record the backend is still writing (`inProgress`), ask whether
 * its turn runs and reattach to it when it does. Returns "reattached"; "running" when the
 * turn runs but the backend names no root agent (an older backend), which is marked as
 * before; "ended"; "cancelled" when `isCurrent()` turned false while it asked; or null when
 * there is nothing to do here, and the caller goes on exactly as before.
 *
 * PLEX-6766: the record's `lastEventId` names an event of its own stream. A record with none
 * holds no event of the turn's stream yet (only its prompt), so the replay starts at the
 * stream's beginning.
 */
export const reattachIfRunning = async (client, conversationId, record, isCurrent = () => true) => {
  if (!(record?.inProgress === true && isConversationId(conversationId))) {
    return null;
  }
  const { running, agentId } = await fetchTurnStatus(conversationId, client.auth);
  if (!isCurrent()) return "cancelled";
  if (running === true && agentId) {
    reattachConversation(client, conversationId, { lastEventId: record.lastEventId || "", agentId });
    return "reattached";
  }
  if (running === true) {
    client.markTurnRunning?.();
    return "running";
  }
  return running === false ? "ended" : null;
};

/**
 * May the stored conversation's turn still be running? A record the backend is writing
 * says so itself (`inProgress`): its last stored turn can be complete while the next one
 * streams, which mayBeMidTurn alone can't see.
 */
export const mayBeRunning = (record) => record?.inProgress === true || mayBeMidTurn(record?.turns);

/**
 * Load a stored conversation into `client` (the Classic chat's restore).
 *
 * When its last turn may still be running (mayBeMidTurn), the trailing tool call is left
 * unrepaired and the backend is asked: a running turn is marked running, so the view says so
 * instead of offering Resume; an ended turn, or one the backend can't answer for, is loaded
 * again exactly as before. Any other conversation loads as before, with no request.
 *
 * A record the backend is still writing is reattached to instead, when its turn runs
 * (reattachIfRunning).
 *
 * Returns `{ result, settled }`: loadConversation's own result, and a promise of "loaded",
 * "reattached", "running", "settled", or "cancelled" (isCurrent() turned false while it
 * asked).
 */
export const loadStoredConversation = (client, conversation, { conversationId, isCurrent = () => true } = {}) => {
  if (!isConversationId(conversationId) || !mayBeRunning(conversation)) {
    return { result: client.loadConversation(conversation), settled: Promise.resolve("loaded") };
  }
  const result = client.loadConversation({
    ...conversation,
    turns: copyOf(conversation.turns),
    subAgentTurns: copyOf(conversation.subAgentTurns),
    deferTrailingToolRepair: true,
  });
  const settled = reattachIfRunning(client, conversationId, conversation, isCurrent).then(async (live) => {
    if (live === "cancelled" || live === "reattached" || live === "running") return live;
    const running = live === "ended" ? false : await fetchTurnRunning(conversationId, client.auth);
    if (!isCurrent()) return "cancelled";
    if (running === true) {
      client.markTurnRunning?.();
      return "running";
    }
    client.loadConversation(conversation);
    return "settled";
  });
  return { result, settled };
};

/**
 * While the engine says its conversation's turn is running elsewhere (client.turnRunning),
 * ask every RUNNING_TURN_POLL_MS whether it has ended, and call `reload(isStale)` once it
 * has, so its result shows without a manual refresh. A turn the backend can't answer for is
 * asked about again. `isStale()` turns true once the view moved on or the mark was cleared.
 */
export const useRunningTurnWatch = (client, { conversationId, enabled = true, reload }) => {
  useEffect(() => {
    if (!enabled || !client || !reload || !isConversationId(conversationId)) return undefined;
    let timer = null;
    let stopped = false;
    const isStale = () => stopped || !client.turnRunning;
    const poll = async () => {
      timer = null;
      const running = await fetchTurnRunning(conversationId, client.auth);
      if (isStale()) return;
      if (running !== false) {
        timer = setTimeout(poll, RUNNING_TURN_POLL_MS);
        return;
      }
      try {
        await reload(isStale);
      } catch (e) {
        console.warn("[live turn] reload after the running turn ended failed", e?.message || e);
      }
    };
    const dispose = reaction(
      () => !!client.turnRunning,
      (running) => {
        if (timer) { clearTimeout(timer); timer = null; }
        // PLEX-6439: a turn the engine knows ended (markTurnEnded) is asked about at once.
        if (running) timer = setTimeout(poll, client.takeRecheckTurnNow?.() ? 0 : RUNNING_TURN_POLL_MS);
      },
      { fireImmediately: true },
    );
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      dispose();
    };
  }, [client, conversationId, enabled, reload]);
};
